import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowRight, ShieldCheck, Zap, Banknote, LineChart, Users, Building,
  AlertCircle, Lock, BadgeCheck, Star, TrendingDown, CreditCard, IndianRupee, PhoneCall,
  CheckCircle2, XCircle, Crown, Flame, Settings, Landmark
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { getPublicPricingPlans } from '../../lib/api';

const stats = [
  { label: 'Loans Disbursed', value: '₹500Cr+' },
  { label: 'Happy Customers', value: '10,000+' },
  { label: 'Partner Banks', value: '50+' },
  { label: 'Cities Covered', value: '100+' }
];

const features = [
  { icon: <Zap className="h-6 w-6 text-emerald-500" />, title: 'Lightning Fast Processing', description: 'Get your loan approved and disbursed in record time with our AI-driven system.' },
  { icon: <ShieldCheck className="h-6 w-6 text-emerald-500" />, title: 'Secure & Confidential', description: 'Bank-grade security ensures your personal and financial data is always protected.' },
  { icon: <Banknote className="h-6 w-6 text-emerald-500" />, title: 'Lowest Interest Rates', description: 'Compare offers from 50+ partner banks to get the best interest rates guaranteed.' },
  { icon: <LineChart className="h-6 w-6 text-emerald-500" />, title: 'Transparent Tracking', description: 'Track your loan application status in real-time through our interactive dashboard.' }
];

const costItems = [
  { icon: <AlertCircle className="h-5 w-5 text-rose-500" />, title: 'No Hidden Processing Fees', desc: 'We disclose every fee upfront. What you see is exactly what you pay – no surprise charges after approval.' },
  { icon: <TrendingDown className="h-5 w-5 text-emerald-500" />, title: 'We Negotiate Rates For You', desc: 'Our team compares 50+ lenders and actively negotiates to slash your interest rate before you sign.' },
  { icon: <CreditCard className="h-5 w-5 text-sky-500" />, title: 'Safe & Secure Payments', desc: 'All payments are routed through RBI-approved channels. Your money never passes through any third party.' },
  { icon: <BadgeCheck className="h-5 w-5 text-indigo-500" />, title: 'We Choose the Best Bank For You', desc: 'Based on your profile, we shortlist only the top 3 lenders with the lowest cost and fastest approval.' },
];

const safetySteps = [
  { step: '01', title: 'You Apply Once', desc: 'Fill a single application. We handle the bank outreach — no multiple form submissions or hard enquiries.' },
  { step: '02', title: 'We Compare & Filter', desc: 'Our system scans 50+ lenders in real-time and filters out any with unfavourable clauses or penalties.' },
  { step: '03', title: 'We Explain Every Clause', desc: 'Before you sign, our advisor walks you through the sanction letter line by line — no confusing fine print.' },
  { step: '04', title: 'Money Straight to Your Account', desc: 'Disbursement goes directly from the bank to you. Zero middlemen, zero risk.' },
];

const testimonials = [
  { name: 'Suresh Menon', city: 'Mumbai', text: 'I was shocked to find my previous bank had added an insurance premium I never agreed to. FinMatrixRQ caught it and got it removed. Saved me ₹45,000!', rating: 5 },
  { name: 'Priya Agarwal', city: 'Bengaluru', text: 'They compared 8 banks for my home loan and found a rate 0.75% lower than what my own bank offered. Highly recommended.', rating: 5 },
  { name: 'Karan Mehta', city: 'Delhi', text: 'The transparency is unbelievable. Every charge was explained before I signed. No surprises after disbursement.', rating: 5 },
];

const Home = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);

  // Map API pricing plan (backend shape) to the UI shape used on the homepage
  const mapApiPlanToUi = (plan) => {
    const f = plan.features || {};

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '';
      const num = Number(value);
      if (!Number.isFinite(num)) return String(value);
      return `₹${num.toLocaleString('en-IN')}`;
    };

    const monthlyPrice = formatCurrency(plan.monthly_price);
    const yearlyPrice = formatCurrency(plan.yearly_price ?? plan.monthly_price * 12);

    const features = [];

    const employeeLimit = f.employee_limit ?? f.max_employees;
    if (employeeLimit) {
      const label = employeeLimit === 'unlimited' || Number(employeeLimit) >= 9999
        ? 'Unlimited Employees'
        : `${employeeLimit} Employees`;
      features.push({ text: label, type: 'yes' });
    }

    const maxAgents = f.max_agents;
    if (maxAgents) {
      const label = maxAgents === 'unlimited'
        ? 'Unlimited Agents'
        : `${maxAgents} Agents`;
      features.push({ text: label, type: 'yes' });
    }
    // Use explicit active/inactive feature lists from API as the main source
    if (Array.isArray(f.active_features)) {
      f.active_features.forEach((text) => {
        features.push({ text, type: 'yes' });
      });
    }
    if (Array.isArray(f.inactive_features)) {
      f.inactive_features.forEach((text) => {
        features.push({ text, type: 'no' });
      });
    }

    // Visual settings (color / tag / popularity) based on plan name, with overrides
    const nameLower = String(plan.name || '').toLowerCase();
    let color = 'blue';
    let popular = false;
    let elite = false;
    let tag = 'Launch Offer';

    if (nameLower.includes('pro')) {
      color = 'emerald';
      popular = true;
      tag = 'Best Value';
    } else if (nameLower.includes('elite')) {
      color = 'purple';
      elite = true;
      tag = 'Premium';
    }

    if (f.badge) {
      tag = f.badge;
    } else if (plan.description) {
      tag = plan.description;
    }

    return {
      name: String(plan.name || '').toUpperCase(),
      monthlyPrice,
      yearlyPrice,
      tag,
      color,
      popular,
      elite,
      features,
    };
  };

  // Load pricing plans from backend for the public site
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const resp = await getPublicPricingPlans();
        const raw =
          Array.isArray(resp?.data) ? resp.data
            : Array.isArray(resp?.data?.items) ? resp.data.items
              : [];
        if (!isMounted || raw.length === 0) return;

        const mapped = [];
        for (const plan of raw) {
          try {
            mapped.push(mapApiPlanToUi(plan));
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to map pricing plan', plan, err);
          }
        }
        if (!isMounted || mapped.length === 0) return;
        setPricingPlans(mapped);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load pricing plans', err);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="flex w-full flex-col">

      {/* ── Hero ── */}
      <section className="relative w-full overflow-hidden bg-slate-900 py-24 lg:py-32 xl:py-48">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1556761175-5973dc0f32d7?q=80&w=3200&auto=format&fit=crop')] bg-cover bg-center bg-no-repeat opacity-20 filter grayscale blur-[2px]"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80"></div>
        <div className="container relative mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center space-y-8 text-center text-white">
            <div className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-400">
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>
              India's #1 Digital DSA Platform
            </div>
            <h1 className="max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl font-['Plus_Jakarta_Sans']">
              Fast-track your financial goals with <span className="text-emerald-400">FinMatrixRQ</span>
            </h1>
            <p className="max-w-2xl text-lg text-slate-300 md:text-xl leading-relaxed">
              Experience the future of lending. Get matched with the perfect loan product from India's top banks, with instant approvals and zero hidden fees.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mt-8">
              <Button size="lg" className="h-14 px-8 text-base bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-semibold" asChild>
                <Link to="/loan-products">Apply for Loan <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-8 text-base border-slate-700 text-white hover:bg-slate-800 hover:text-white" asChild>
                <Link to="/become-agent">Become a Partner</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="w-full py-12 bg-emerald-500">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-emerald-400/30">
            {stats.map((stat, index) => (
              <div key={index} className="flex flex-col items-center justify-center text-center px-4">
                <h3 className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.value}</h3>
                <p className="text-emerald-50 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="w-full py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl font-['Plus_Jakarta_Sans'] mb-4">Why Choose FinMatrixRQ?</h2>
            <p className="text-muted-foreground text-lg">We leverage cutting-edge technology to simplify the borrowing process, making it faster, cheaper, and completely transparent.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow bg-white dark:bg-slate-800">
                <CardContent className="p-8">
                  <div className="h-12 w-12 rounded-2xl bg-emerald-100 dark:bg-emerald-500/10 flex items-center justify-center mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEW: Zero Hidden Costs Guarantee ── */}
      <section className="w-full py-24 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Our Promise to You</span>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl font-['Plus_Jakarta_Sans'] mb-4">
              We help you avoid <span className="text-rose-500">every hidden charge</span>
            </h2>
            <p className="text-muted-foreground text-lg">Lenders sometimes bury fees in fine print. Our advisors fight on your behalf — so you always keep more money in your pocket.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {costItems.map((item, i) => (
              <div key={i} className="group rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="h-11 w-11 rounded-xl bg-white dark:bg-slate-900 shadow-sm flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-2 text-sm leading-snug">{item.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEW: How We Protect Your Money ── */}
      <section className="w-full py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-emerald-500/15 text-emerald-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Safe & Transparent Process</span>
              <h2 className="text-3xl font-bold sm:text-4xl font-['Plus_Jakarta_Sans'] mb-6">
                How we protect your money at every step
              </h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                We've built a 4-step process that keeps you informed, protected, and in complete control of your loan journey. No surprises. Ever.
              </p>
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                <Lock className="h-8 w-8 text-emerald-400 shrink-0" />
                <div>
                  <p className="text-emerald-400 font-semibold text-sm">Bank-Grade Security</p>
                  <p className="text-slate-400 text-xs mt-0.5">256-bit SSL encryption. Your documents and data are never shared without your explicit consent.</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {safetySteps.map((s, i) => (
                <div key={i} className="flex gap-5 p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <span className="text-2xl font-black text-emerald-500 shrink-0 leading-none">{s.step}</span>
                  <div>
                    <h4 className="font-bold text-white mb-1">{s.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── NEW: Testimonials ── */}
      <section className="w-full py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl font-['Plus_Jakarta_Sans'] mb-4">Real customers. Real savings.</h2>
            <p className="text-muted-foreground text-lg">Hear from borrowers who saved thousands with our guidance.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 p-7 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex gap-0.5 mb-5">
                  {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="h-4 w-4 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center font-bold text-emerald-600 dark:text-emerald-400 text-sm">{t.name[0]}</div>
                  <div><p className="font-semibold text-slate-900 dark:text-white text-sm">{t.name}</p><p className="text-xs text-slate-400">{t.city}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEW: Pricing Plans ── */}
      <section className="w-full py-24 bg-slate-50 dark:bg-slate-900/50 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500 rounded-full blur-[100px] opacity-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-[100px] opacity-10"></div>
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16 relative">
            <div className="flex justify-center items-center gap-4 mb-4">
              <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-sm font-bold px-4 py-1.5 rounded-full border border-amber-200">
                <BadgeCheck className="h-4 w-4" /> 30 Day Free Trial
              </span>
              <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 text-sm font-bold px-4 py-1.5 rounded-full border border-emerald-200">
                <CheckCircle2 className="h-4 w-4" /> No Setup Fee
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight font-['Plus_Jakarta_Sans'] mb-4">
              <span className="text-blue-700 dark:text-blue-500">DSA CORE</span> <span className="text-rose-600 bg-rose-100 dark:bg-rose-900/30 px-4 py-1 rounded-xl uppercase inline-block italic rotate-[-2deg]">Pricing Plan</span>
            </h2>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px bg-slate-300 dark:bg-slate-700 w-16"></div>
              <p className="text-slate-600 dark:text-slate-400 font-semibold italic text-lg lg:text-xl">Complete DSA Management Software</p>
              <div className="h-px bg-slate-300 dark:bg-slate-700 w-16"></div>
            </div>

            <div className="flex items-center justify-center mt-10 mb-4">
              <span className={`text-base font-bold transition-colors ${!isYearly ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'}`}>Monthly</span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className="mx-4 relative inline-flex h-8 w-16 items-center rounded-full bg-slate-200 dark:bg-slate-700 transition-colors focus:outline-none"
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-emerald-500 transition-transform ${isYearly ? 'translate-x-9' : 'translate-x-1'}`} />
              </button>
              <span className={`text-base font-bold flex items-center gap-2 transition-colors ${isYearly ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'}`}>
                Yearly <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full shadow-sm">Save 20%</span>
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-end max-w-6xl mx-auto mb-16">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                className={`relative rounded-3xl bg-white dark:bg-slate-800 shadow-xl border-t-[8px] flex flex-col ${plan.popular ? 'border-t-emerald-600 scale-100 lg:scale-105 z-10 shadow-2xl relative' :
                  plan.elite ? 'border-t-purple-600' : 'border-t-blue-600'
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-6 left-0 w-full flex justify-center">
                    <span className="bg-emerald-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <Flame className="h-3 w-3" /> Most Popular
                    </span>
                  </div>
                )}
                {plan.elite && (
                  <div className="absolute -top-12 left-0 w-full flex justify-center pb-4">
                    <Crown className="h-8 w-8 text-amber-500 drop-shadow-md" />
                  </div>
                )}

                <div className={`text-center p-6 ${plan.popular ? 'bg-gradient-to-b from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800 rounded-t-2xl' :
                  plan.elite ? 'bg-gradient-to-b from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-800 rounded-t-2xl' :
                    'bg-gradient-to-b from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800 rounded-t-2xl'
                  }`}>
                  <h3 className={`text-xl font-bold uppercase tracking-wider mb-2 ${plan.popular ? 'text-emerald-700 dark:text-emerald-400' :
                    plan.elite ? 'text-purple-700 dark:text-purple-400' :
                      'text-blue-700 dark:text-blue-400'
                    }`}>
                    {plan.name}
                  </h3>
                  <div className="flex justify-center items-end gap-1 mb-2">
                    <span className="text-4xl font-extrabold text-slate-900 dark:text-white">
                      {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-slate-500 mb-1 font-medium">{isYearly ? '/ Year' : '/ Month'}</span>
                  </div>

                  <div className={`inline-block px-4 py-1 text-sm font-bold rounded shadow-sm text-white mb-4 ${plan.popular ? 'bg-amber-500' :
                    plan.elite ? 'bg-amber-400 text-slate-900' :
                      'bg-rose-600'
                    }`}>
                    {plan.tag}
                  </div>

                  {!isYearly ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700 pt-4">
                      <span className="font-bold line-through op">{plan.yearlyPrice} / Yearly</span>
                      <span className="text-emerald-600 font-bold bg-emerald-100 dark:bg-emerald-900/30 px-2 rounded">20% OFF</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 border-t border-emerald-100 dark:border-emerald-900/40 pt-4">
                      <span className="font-bold">You are saving 20% today!</span>
                    </div>
                  )}
                </div>

                <div className="p-6 pt-2 flex-grow">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left sm:justify-between py-1 border-b border-slate-50 dark:border-slate-700/50 last:border-0 gap-2">
                        <span className={`text-sm font-medium ${feature.type === 'no' ? 'text-slate-400 line-through decoration-slate-300' : 'text-slate-700 dark:text-slate-200'}`}>
                          {feature.text}
                        </span>
                        {feature.type === 'yes' ? (
                          <CheckCircle2 className={`h-5 w-5 shrink-0 mx-auto sm:mx-0 ${plan.popular ? 'text-emerald-500' : plan.elite ? 'text-emerald-500' : 'text-emerald-500'
                            }`} />
                        ) : (
                          <XCircle className="h-5 w-5 text-rose-400 shrink-0 mx-auto sm:mx-0 opacity-70" />
                        )}
                      </li>
                    ))}
                  </ul>

                  <Button asChild className={`w-full mt-8 rounded-full h-12 font-bold text-lg shadow-md transition-transform hover:scale-105 ${plan.popular ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
                    plan.elite ? 'bg-purple-700 hover:bg-purple-800 text-white' :
                      'bg-blue-700 hover:bg-blue-800 text-white'
                    }`}>
                    <Link to={`/checkout?plan=${plan.name.toLowerCase().replace(' ', '-')}&billing=${isYearly ? 'yearly' : 'monthly'}`}>
                      Get Started {plan.popular || plan.elite ? <ArrowRight className="ml-2 h-5 w-5" /> : null}
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Add-Ons Strip */}
          <div className="max-w-4xl mx-auto rounded-full bg-amber-50 dark:bg-amber-900/10 border-2 border-amber-200 dark:border-amber-700/50 p-2 sm:p-4 mb-12 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 relative mt-20">
            {/* <div className="absolute -top-4 sm:top-auto sm:-left-4 bg-amber-500 text-white font-bold px-4 py-1 rounded-full shadow-md text-sm">
              Add-Ons (Extra)
            </div> */}
            <div className="flex-1 flex flex-wrap items-center justify-center sm:justify-center gap-6 pt-4 sm:pt-0 pl-0 sm:pl-28 w-full">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Customization <span className="text-amber-600">₹15,000</span></span>
              </div>
              <div className="flex items-center gap-2">
                <Landmark className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Bank API <span className="text-amber-600">₹25,000</span></span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">White Label <span className="text-amber-600">₹40,000</span></span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 sm:gap-8 text-sm font-bold text-slate-700 dark:text-slate-300">
            <span className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> 30 Day Free Trial</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> No Setup Fee</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Instant Support</span>
            <span className="flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-amber-500" /> Best Value Guaranteed</span>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="w-full py-24 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="rounded-3xl bg-slate-900 dark:bg-slate-800 p-8 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-emerald-500 rounded-full blur-3xl opacity-20"></div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-blue-500 rounded-full blur-3xl opacity-20"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-12">
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Plus_Jakarta_Sans']">Join our network of successful partners</h2>
                <p className="text-slate-300 text-lg mb-8 max-w-xl">Become a FinMatrixRQ certified agent and start earning industry-leading commission payouts with zero upfront investment.</p>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 h-12 px-8 w-full sm:w-auto" asChild>
                    <Link to="/become-agent">Join as Agent</Link>
                  </Button>
                  <Button size="lg" variant="link" className="text-emerald-400 hover:text-emerald-300" asChild>
                    <Link to="/login">Agent Login <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </div>
              </div>
              <div className="flex-1 w-full max-w-md">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="h-14 w-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Users className="h-7 w-7 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold flex items-center gap-2">Premium Agent Tier <Zap className="h-4 w-4 text-amber-400" /></h4>
                      <p className="text-slate-400 text-sm">Up to 2.5% Commission</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 w-3/4 rounded-full"></div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Monthly Target</span>
                      <span className="text-white font-medium">₹75L / ₹1Cr</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
